import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Video, FileDown, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import api, { cookieUtils } from '../API/ApiService';
import LoaderOverlay from '../Loader/LoaderOverlay';
import VideoPlayer from '../MediaPlayer/VideoPlayer';
import {s3BucketUrl} from '../../config';
import { useAuth } from '../Auth/useAuth';

const EsignRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const mountedRef = useRef(false);
    const {checkToken} = useAuth();

    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        checkToken();
    }, [checkToken]);

    const fetchRequests = async (page) => {
        try {
            // Only show full loader on initial load
            if (page === 0) {
                setLoading(true);
            } else {
                setIsLoadingMore(true);
            }
            
            const sdkToken = cookieUtils.get('sdkToken');
            if(!sdkToken) {
                setLoading(false);
                toast.error('App Keys expired')
                return;
            }
            const response = await api.post('/esign-all-document-list', {
                page: page,
                per_page: 10,
                getUserVideoIds:true
            }, 'SDK');
            
            if (response?.template?.data && mountedRef.current) {
                const finalResponse = response?.template?.data;
                setRequests(finalResponse || []);
                setHasMore(finalResponse.length === 10); // Check if we have more pages
                setLoading(false);
            }
        } catch (error) {
            if (mountedRef.current) {
                console.error('Error fetching requests:', error);
                toast.error('Failed to load requests');
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setIsLoadingMore(false);
            }
        }
    };

    useEffect(() => {
        if (mountedRef.current) return;
        mountedRef.current = true;
        fetchRequests(currentPage);
        return () => {
            mountedRef.current = false;
        };
    }, [currentPage]);

    const handleVideoClick = (videoUrl) => {
        const awsPathForSignerVideo = `${s3BucketUrl}/uploadedvideos/${videoUrl}`
        setSelectedVideo(awsPathForSignerVideo);
    };
    
    const deleteRequest = async (docId) => {
        try { 
            const deleteResponse = await api.post('/esign-delete-request', {
                docId: docId,
            }, 'SDK');
            console.log(deleteResponse);
            if (deleteResponse.status === false) {
                setRequests(requests.filter((item)=>item.id !== docId))
            }
        } catch (error) {
           console.error(error);
           toast.error('Could not delete request'); 
        }
    }

    const handleDownloadPdf = async (docId) => {
        try {
            toast.success('Opening Document');
            const docUrl = `${s3BucketUrl}/common/signedpdf/${docId}/${docId}.pdf`;
            const response = await fetch(docUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank'); // Opens PDF in a new tab
        
            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
            }, 1000);
            
            // const downloadLink = document.createElement('a');
            // downloadLink.href = window.URL.createObjectURL(blob);
            // downloadLink.download = `document-${docId}.pdf`;
            
            // document.body.appendChild(downloadLink);
            // downloadLink.click();
            // document.body.removeChild(downloadLink);
            
            // window.URL.revokeObjectURL(downloadLink.href);
            
            // toast.success('Document downloaded successfully');
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download document');
        }
    };

    const handleNextPage = async () => {
        if (hasMore) {
            setIsLoadingMore(true);
            setCurrentPage(prev => prev + 1);
            await fetchRequests(currentPage + 1);
            setIsLoadingMore(false);
        }
    };

    const handlePrevPage = async () => {
        if (currentPage > 0) {
            setIsLoadingMore(true);
            setCurrentPage(prev => prev - 1);
            await fetchRequests(currentPage - 1);
            setIsLoadingMore(false);
        }
    };

    if (loading) {
        return <LoaderOverlay />;
    }

    return (
        <div>
            {selectedVideo && (
                <VideoPlayer
                    videoUrl={selectedVideo}
                    onClose={() => setSelectedVideo(null)}
                />
            )}
            
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">KYC Requests</h2>
                <p className="text-gray-600">View all your requests here</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-800">Your Requests</h3>
                </div>
                
                <div className="divide-y divide-gray-200">
                    {requests.map((request) => (
                        <div 
                            key={request._id} 
                            className="px-6 py-4 hover:bg-gray-50 transition duration-200"
                        >
                            <div className="flex justify-between items-center">
                                <div className="flex-grow">
                                    <h4 className="text-sm font-medium text-gray-800">
                                        {request.documentName}
                                    </h4>
                                    <div className="flex gap-4 text-sm text-gray-600 mt-1">
                                        <p>Type: <span className="font-medium">
                                            {request.withimage === "wi" ? "AV" : "NonAV"}
                                        </span></p>
                                        <p>From: <span className="font-medium">{request.fromemail}</span></p>
                                        <p>To: <span className="font-medium">{request.toemail}</span></p>
                                        <p>Status: <span className={`font-medium ${
                                            request.actionrequired === "Signed" ? 'text-green-600' : 
                                            'text-yellow-600'
                                        }`}>
                                            {request.actionrequired === 'Prev signer signed pending' ? 'Not Signed' : request.actionrequired}
                                        </span></p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6">
                                    {request.uservideo && (
                                        <button
                                            onClick={() => handleVideoClick(request.uservideo)}
                                            className="p-2 text-blue-600 hover:text-blue-700 rounded-full hover:bg-blue-50"
                                            title="View Recording"
                                        >
                                            <Video className="h-5 w-5" />
                                        </button>
                                    )}
                                    {request.actionrequired === 'Signed' && (
                                        <button
                                            onClick={() => handleDownloadPdf(request.documentid)}
                                            className="p-2 text-blue-600 hover:text-blue-700 rounded-full hover:bg-blue-50"
                                            title="Download PDF"
                                        >
                                            <FileDown className="h-5 w-5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => deleteRequest(request.id)}
                                        className="p-2 text-red-500 hover:text-red-600 rounded-full hover:bg-red-50"
                                        title="Delete Request"
                                    >
                                        <Trash2 className="h-5 w-5" />
                                    </button>
                                    <div className="text-sm text-gray-500">
                                        Created: {new Date(request.dateadded).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {requests.length === 0 && (
                        <div className="px-6 py-8 text-center text-gray-500">
                            No requests found.
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 flex justify-center items-center gap-4">
                <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 0 || isLoadingMore || requests.length === 0}
                    className={`p-2 rounded-full ${
                        currentPage === 0 || isLoadingMore || requests.length === 0
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-blue-600 hover:bg-blue-50'
                    }`}
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-sm text-gray-600 flex items-center gap-2">
                    Page {currentPage + 1}
                    {isLoadingMore && (
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    )}
                </span>
                <button
                    onClick={handleNextPage}
                    disabled={!hasMore || isLoadingMore || requests.length === 0}
                    className={`p-2 rounded-full ${
                        !hasMore || isLoadingMore || requests.length === 0
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-blue-600 hover:bg-blue-50'
                    }`}
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
};

export default EsignRequests;